/*
 * YipYip — product gallery rail helper
 * ---------------------------------------------------------------------------
 * The redesign turns the desktop thumbnail strip into a VERTICAL rail beside
 * the hero (yy-product.css section 3). Two things in the theme assume a
 * horizontal one, and this file covers both. It adds no new markup hooks and
 * never writes `aria-current` — media-gallery.js owns that attribute; this
 * only reads it.
 *
 * 1. SCROLL FOLLOW. `MediaGallery.setActiveThumbnail` (media-gallery.js:80)
 *    ends in `slider.scrollTo({ left: thumbnail.offsetLeft })`, and
 *    `SliderComponent.isSlideVisible` (global.js:832) measures with
 *    clientWidth / scrollLeft / offsetLeft. In a column every thumbnail shares
 *    an offsetLeft, so that call resolves to `scrollTo({ left: 0 })` — a
 *    harmless no-op that simply never scrolls. Without this, picking a colour
 *    whose photo is the tenth of fourteen leaves the rail parked at the top
 *    with the selection out of view.
 *
 * 2. THE COUNTER. The comp puts "3 / 14" over the hero. Dawn's own counter
 *    (`.slider-counter--current`) is only emitted when mobile_pagination is
 *    `numeric`, lives under the gallery rather than on it, and is driven from
 *    `scrollLeft / sliderItemOffset` — the same horizontal assumption. The
 *    position is read off the rail instead, which is true at every width and
 *    for both directions.
 *
 * 3. THE STRIP LANDS ON THE WRONG SLIDE. Under 990px the viewer is still a
 *    horizontal `scroll-snap-type: x mandatory` strip of every slide. Fourteen
 *    lazy images resolving one after another keep changing its content size,
 *    and the engine re-snaps to whatever is nearest each time — measured
 *    landing on slide 8 with `.is-active`, the rail's `aria-current` and the
 *    counter all correctly saying slide 1. Realigning to the active slide once
 *    on load, and again whenever the selection deliberately changes, makes the
 *    opening state deterministic. It is NOT run on scroll, so a swipe is never
 *    fought.
 *
 * Degrades to nothing without JS: the counter is server-rendered at its
 * opening value and the rail is a plain scroll container.
 */
(function () {
  'use strict';

  var REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  function visibleItems(rail) {
    // `.thumbnail-list_item--variant:not(:first-child)` is display:none when the
    // merchant hides variant duplicates, and a hidden thumbnail is not a
    // position anyone can count to.
    return Array.prototype.filter.call(rail.querySelectorAll('.thumbnail-list__item'), function (li) {
      return li.getClientRects().length > 0;
    });
  }

  // Puts the active slide back under the viewport of the horizontal strip.
  // Above 990px only one slide is rendered, so there is nothing to scroll and
  // this returns immediately.
  function alignViewer(gallery) {
    var list = gallery.querySelector('[id^="GalleryViewer"] .product__media-list');
    if (!list || list.scrollWidth - list.clientWidth < 2) return;

    var active = list.querySelector('.product__media-item.is-active') || list.firstElementChild;
    if (!active) return;

    // Rects rather than offsetLeft: offsetLeft is measured from the nearest
    // POSITIONED ancestor, and yy-product.css makes the slider-component
    // relative so the counter can sit on the photograph.
    var pad = parseFloat(window.getComputedStyle(list).scrollPaddingLeft) || 0;
    var delta = active.getBoundingClientRect().left - list.getBoundingClientRect().left - pad;
    if (Math.abs(delta) < 2) return;

    list.scrollLeft += delta;
  }

  function connect(gallery) {
    var rail = gallery.querySelector('[id^="GalleryThumbnails"] .thumbnail-list');
    if (!rail) return;

    var counter = gallery.querySelector('[data-yy-gallery-counter]');
    var current = counter && counter.querySelector('[data-yy-gallery-current]');
    var total = counter && counter.querySelector('[data-yy-gallery-total]');
    var queued = false;

    function sync() {
      queued = false;
      var items = visibleItems(rail);
      if (!items.length) return;

      var index = -1;
      for (var i = 0; i < items.length; i++) {
        if (items[i].querySelector('button[aria-current]')) {
          index = i;
          break;
        }
      }
      if (index < 0) return;

      if (current) current.textContent = String(index + 1);
      if (total) total.textContent = String(items.length);

      alignViewer(gallery);

      // Only ever drive the axis the rail actually scrolls on, so this stays
      // inert while the rail is horizontal (mobile, and tablet under 990px)
      // and media-gallery.js's own scroll keeps working there.
      if (rail.scrollHeight - rail.clientHeight < 2) return;
      var item = items[index];
      var top = item.offsetTop - (rail.clientHeight - item.offsetHeight) / 2;
      rail.scrollTo({
        top: Math.max(0, top),
        behavior: REDUCE.matches ? 'auto' : 'smooth',
      });
    }

    function schedule() {
      if (queued) return;
      queued = true;
      window.requestAnimationFrame(sync);
    }

    // setActiveThumbnail clears aria-current from every button before setting
    // it on one, so a single click fires this several times; coalescing on a
    // frame collapses that to one read.
    new MutationObserver(schedule).observe(rail, {
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-current'],
    });

    sync();

    // The drift happens as the lazy images resolve, so one pass at
    // DOMContentLoaded is not enough — `load` fires once they are in.
    window.addEventListener('load', function () {
      alignViewer(gallery);
    });
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(connect);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
